import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MessageBusService } from '../../../../shared-components/message-bus.service';
import { ServerTimeMessageDto } from '../../../../shared-components/shell-communication/shell-dto-protocol';
import {
   DRIFT_THRESHOLD_SECONDS,
   DriftState,
   DriftStateSnapshot,
   SAMPLE_INTERVAL_MS,
   STARTUP_GRACE_MS,
   VISIBILITY_GRACE_MS
} from './drift-detection.constants';

// CR-20260424-003 — UI render-loop lag indicator.
// Detects when the rendered server-time clock falls behind real time
// (either because messages stop flowing OR because the main thread is
// saturated and the detector itself fires late).
//
// CR-20260515-003 — collapsed to two-state machine IDLE -> WARNING after
// LEAK-1 removed auto-logout on CONFIRMED. WARNING is the sole orange-
// border state; recovery is automatic when the drift sample drops back
// below threshold. Catastrophic-slip fast-path, ESCALATION_INTERVAL_SECONDS,
// and the CONFIRMED branch are gone — they only existed to reach the
// now-defunct latched terminal. STARTUP_GRACE_MS engaged at start() so the
// login burst can't trip WARNING before the main thread has settled.

// CR-20260508-001 — DriftDetectionService is being superseded by
// SessionSyncOverlayService. Disabled here for the successor experiment;
// flip back to false to re-enable the orange-border WARNING behavior.
// When the successor is validated and accepted, this service should be
// removed entirely along with its consumers in ServerTimePanelComponent.
const DRIFT_DETECTION_DISABLED = false;

@Injectable({
   providedIn: 'root'
})
export class DriftDetectionService {

   constructor(
      private readonly _messageBus: MessageBusService,
      private readonly _ngZone: NgZone,
   ) {
      // Side-effect-free per memory rule (feedback_di_side_effect_free_ctors).
      this._unsubscriber = new Subject<any>();
      this._driftState$ = new BehaviorSubject<DriftStateSnapshot>({
         state: 'IDLE',
         observedDriftMs: 0,
         timestamp: Date.now()
      });
   }

   //

   private readonly _unsubscriber: Subject<any>;
   private readonly _driftState$: BehaviorSubject<DriftStateSnapshot>;

   private _started = false;
   private _intervalHandle: any = undefined;
   private _expectedFireTime = 0;
   private _lastMessage: ServerTimeMessageDto | undefined;
   private _slipMs = 0;
   private _injectedDriftMs = 0;
   private _state: DriftState = 'IDLE';
   private _graceUntil = 0;
   private _wasActive = true;

   //

   get driftState$(): Observable<DriftStateSnapshot> {
      return this._driftState$.asObservable();
   }

   get currentState(): DriftState {
      return this._state;
   }

   get currentDriftMs(): number {
      if (this.isFakeTimeMode) {
         return 0;
      }
      return Math.max(this.messageGapMs, this._slipMs, this._injectedDriftMs);
   }

   get messageGapMs(): number {
      if (!this._lastMessage) {
         return 0;
      }
      return Math.max(0, Date.now() - +this._lastMessage.time);
   }

   get scheduleSlipMs(): number {
      return this._slipMs;
   }

   get isFakeTimeMode(): boolean {
      return this._lastMessage ? this._lastMessage.isFakeTime === true : false;
   }

   //

   start(): void {
      if (this._started) {
         return;
      }
      if (DRIFT_DETECTION_DISABLED) {
         console.info('[DriftDetectionService] DISABLED via CR-20260508-001 feature flag '
            + '(SessionSyncOverlayService is the successor)');
         this._started = true;
         return;
      }
      this._started = true;

      // CR-20260515-003 — startup grace. /app mount runs heavy synchronous
      // work (DI verify, panel hydrate, ag-Grid + DevExtreme widgets) that
      // can starve the 1Hz tick on slow clients. Suppress state transitions
      // for STARTUP_GRACE_MS so the burst can't trip WARNING before the
      // main thread settles.
      this._graceUntil = performance.now() + STARTUP_GRACE_MS;

      this._messageBus
         .of<ServerTimeMessageDto>('ServerTimeMessageDto')
         .pipe(takeUntil(this._unsubscriber))
         .subscribe(x => this._lastMessage = x.payload);

      this.startScheduler();
      this.attachDebugHook();
      console.info('[DriftDetectionService] Started (polling-based activity detection, '
         + 'startupGraceMs=' + STARTUP_GRACE_MS + ', initial active=' + this.isActive() + ')');
   }

   private startScheduler(): void {
      if (this._intervalHandle !== undefined) {
         return;
      }
      this._ngZone.runOutsideAngular(() => {
         this._expectedFireTime = performance.now() + SAMPLE_INTERVAL_MS;
         this._intervalHandle = setInterval(() => this.tick(), SAMPLE_INTERVAL_MS);
      });
   }

   private stopScheduler(): void {
      if (this._intervalHandle !== undefined) {
         clearInterval(this._intervalHandle);
         this._intervalHandle = undefined;
      }
   }

   // CR-20260424-003 — pause detection while tab is hidden.
   //
   // ARCHITECTURE: polling, not events.
   // Earlier iterations used document.visibilitychange + window.blur/focus
   // listeners. Empirically, in the user's environment, NO browser event
   // fires when they switch tabs. Polling reads document state directly
   // each scheduler tick (1 Hz) — browser is the source of truth, no event
   // dispatch required. Detection latency ≤ 1 second.
   //
   // CR-20260515-003 — `document.hidden` only; `hasFocus()` dropped.
   // Aligns with `SessionSyncOverlayService.computeIsActive` convention and
   // the standard documented in `feedback_devtools_focus_suppresses_blur.md`
   // / CR-20260512-001 SIP (b): combining `!hidden && hasFocus()` conflates
   // "tab hidden" with "tab unfocused." The earlier heuristic silenced the
   // detector whenever DevTools, Notepad, or any other window had focus on
   // top of the visible tab — including DevTools-driven QA via
   // `__etsDebugDrift.inject`, which is why AC-2 was unreachable. The tab
   // is "active" iff it's visible to the user; keyboard focus is irrelevant.
   private isActive(): boolean {
      return typeof document !== 'undefined' && !document.hidden;
   }

   private onHidden(): void {
      // Polling architecture: don't stop the scheduler — it must keep ticking
      // to detect when the user returns. State machine is short-circuited in
      // tick() when isActive() returns false. CONFIRMED is left untouched
      // (auto-logout already in flight).
      console.info('[DriftDetectionService] window inactive — state machine paused (hidden='
         + document.hidden + ', hasFocus=' + (document.hasFocus ? document.hasFocus() : 'n/a') + ')');
   }

   private onVisible(): void {
      // Full clean slate via reset() — clears _state, _slipMs, _lastMessage,
      // _injectedDriftMs, and emits IDLE on the BehaviorSubject. Without
      // clearing _injectedDriftMs, a debug injection that was active before
      // the tab was hidden survives the transition and re-trips WARNING on
      // the first post-grace tick. (The same applies to any stale
      // measurement; reset() handles all of them in one place.)
      this.reset();
      this._graceUntil = performance.now() + VISIBILITY_GRACE_MS;
      // Scheduler is already running (polling architecture).
      console.info('[DriftDetectionService] window active — grace period active for ' + VISIBILITY_GRACE_MS + 'ms');
   }

   stop(): void {
      this.stopScheduler();
      if (this._unsubscriber && !this._unsubscriber.closed) {
         this._unsubscriber.next();
         this._unsubscriber.complete();
      }
      this._started = false;
   }

   // CR-20260424-003 — reset all per-session state without re-instantiating
   // the singleton. Called on re-login (ServerTimePanelComponent.ngOnInit)
   // to clear:
   //   (a) latched CONFIRMED state from prior session — otherwise the
   //       BehaviorSubject replays CONFIRMED to the next subscriber and
   //       auto-logout fires again immediately.
   //   (b) stale _lastMessage — otherwise messageGapMs against an old
   //       ServerTimeMessageDto.time re-trips WARNING on first tick.
   reset(): void {
      const prevState = this._state;
      this._state = 'IDLE';
      this._slipMs = 0;
      this._lastMessage = undefined;
      this._injectedDriftMs = 0;
      this._expectedFireTime = performance.now() + SAMPLE_INTERVAL_MS;
      this._driftState$.next({
         state: 'IDLE',
         observedDriftMs: 0,
         timestamp: Date.now()
      });
      if (prevState !== 'IDLE') {
         console.info('[DriftDetectionService] reset — ' + prevState + ' → IDLE (transients cleared)');
      }
   }

   injectDrift(ms: number): void {
      this._injectedDriftMs = Math.max(0, ms);
   }

   clearInjection(): void {
      this._injectedDriftMs = 0;
   }

   //

   private tick(): void {
      const actualFireTime = performance.now();
      this._slipMs = Math.max(0, actualFireTime - this._expectedFireTime);
      this._expectedFireTime = actualFireTime + SAMPLE_INTERVAL_MS;

      // Polling activity check — directly reads browser state, no event
      // dispatch required. Detects transitions even when the browser
      // does not fire visibilitychange / blur / focus.
      const nowActive = this.isActive();
      if (nowActive !== this._wasActive) {
         this._wasActive = nowActive;
         if (nowActive) {
            this.onVisible();
         } else {
            this.onHidden();
         }
      }

      // While window inactive, skip state machine entirely. Scheduler keeps
      // ticking so we detect return. State is preserved (CONFIRMED stays
      // CONFIRMED; WARNING stays WARNING; IDLE stays IDLE).
      if (!nowActive) {
         return;
      }

      // Grace period after activity return — measurements continue but
      // state machine is short-circuited. Lets the post-foreground
      // processing burst settle before any escalation can fire.
      if (actualFireTime < this._graceUntil) {
         return;
      }

      const driftMs = this.currentDriftMs;
      const thresholdMs = DRIFT_THRESHOLD_SECONDS * 1000;
      const prevState = this._state;

      // CR-20260515-003 — two-state machine. WARNING is the sole orange-
      // border state; recovery edge fires automatically when the drift
      // sample drops back below threshold. No latched terminal: a single
      // starved tick during /app load can no longer permanently light
      // the indicator.
      if (this._state === 'IDLE') {
         if (driftMs >= thresholdMs) {
            this._state = 'WARNING';
         }
      } else if (driftMs < thresholdMs) {
         this._state = 'IDLE';
      }

      if (this._state !== prevState) {
         this.logTransition(prevState, this._state, driftMs);
         this.emitState(driftMs);
      }
   }

   private emitState(observedDriftMs: number): void {
      this._driftState$.next({
         state: this._state,
         observedDriftMs,
         timestamp: Date.now()
      });
   }

   private logTransition(prev: DriftState, next: DriftState, driftMs: number): void {
      const drift = (driftMs / 1000).toFixed(1);
      const gap = (this.messageGapMs / 1000).toFixed(1);
      const slip = (this._slipMs / 1000).toFixed(1);

      if (next === 'WARNING') {
         console.info(`[DriftDetectionService] WARNING entered (drift=${drift}s, gap=${gap}s, slip=${slip}s)`);
      } else if (next === 'IDLE' && prev === 'WARNING') {
         console.info(`[DriftDetectionService] WARNING cleared (drift recovered to ${drift}s)`);
      }
   }

   //

   private attachDebugHook(): void {
      // FR-10 / WB-14 — global debug hook for QA/support. Present in ALL builds
      // (DEV/STAGING/PROD). DevTools-only access; zero UI surface.
      const self = this;
      (window as any).__etsDebugDrift = {
         inject(ms: number): void { self.injectDrift(ms); },
         clear(): void { self.clearInjection(); },
         get state(): DriftState { return self._state; },
         get messageGapMs(): number { return self.messageGapMs; },
         get scheduleSlipMs(): number { return self._slipMs; },
         get isFakeTimeMode(): boolean { return self.isFakeTimeMode; },
         get currentDriftMs(): number { return self.currentDriftMs; }
      };
   }
}
